import React, { useCallback, useEffect, useState, useMemo } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext.jsx';
import { Label } from '../Label/index.jsx';
import { findStarMapImageUrl, starMapImageUrlToFile } from '../../utilities/starMapUtils.js';

import styles from './personalisation-upload.module.css';
import { PhotoEditor } from '../PhotoEditor/index.js';
import { FileInput } from '../FileInput/index.jsx';
import { storefront } from '../../gateway/client.js';

const getMediaImageQuery = `
  query GetMediaImage($id: ID!) {
    node(id: $id) {
      ... on MediaImage {
        image {
          url
        }
      }
    }
  }
`;

/**
 * PersonalisationImageUploadSlot component for handling image uploads in personalisation steps.
 * @param {Object} props - The component props.
 * @param {import('../../gateway/personalisationData.js').Slot} props.slot - The slot configuration object containing all slot properties.
 * @param {boolean} [props.shouldShowStarMapSlot] - Whether to show star map slot functionality and auto-open editor.
 * @returns {React.ReactElement} The rendered PersonalisationImageUploadSlot component.
 */
export const PersonalisationImageUploadSlot = ({ slot, shouldShowStarMapSlot = false }) => {
  const { updateSelections, selections, personalisationSteps, currentStepId } = usePersonalisationContext();
  const step = personalisationSteps?.get(currentStepId);

  // State for storing the mask SVG content.
  const [maskSvgBlob, setMaskSvg] = useState(null);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(step.baseVisualiserImage);
  const [shouldAutoOpen, setShouldAutoOpen] = useState(false);
  const [resetScaleTrigger, setResetScaleTrigger] = useState(0);

  // Get existing selection for this slot.
  const existingSelection = selections.slots?.find((selection) => selection.id === slot.id);
  const existingFile = existingSelection?.selected instanceof File ? existingSelection.selected : null;
  const requiresMasking = slot.targetImagePlaceholder !== null;

  /**
   * Finds star map image URL from any slot in the context when shouldShowStarMapSlot is enabled.
   *
   * @returns {string | null} The star map image URL (data URL or regular URL) or null if not found.
   */
  const starMapImageUrl = useMemo(() => {
    if (!shouldShowStarMapSlot) {
      return null;
    }
    return findStarMapImageUrl(selections.slots);
  }, [shouldShowStarMapSlot, selections.slots]);

  /**
   * Converts star map image URL to File object when shouldShowStarMapSlot is enabled.
   * This File will be passed to PhotoEditor to auto-open with the star map image.
   */
  const starMapFile = useMemo(() => {
    if (!shouldShowStarMapSlot || !starMapImageUrl) {
      return null;
    }
    return starMapImageUrlToFile(starMapImageUrl, 'star-map.png');
  }, [shouldShowStarMapSlot, starMapImageUrl]);

  // Fetch and set the mask SVG from the target image placeholder.
  useEffect(() => {
    if (requiresMasking) {
      (async () => {
        try {
          const response = await fetch(slot.targetImagePlaceholder.url);
          if (!response.ok) {
            throw new Error(`Failed to fetch mask: ${response.statusText}`);
          }

          const imageBlob = await response.blob();
          setMaskSvg(imageBlob);
        } catch (error) {
          console.error('Failed to fetch mask:', error);
        }
      })();
    }
  }, [slot.targetImagePlaceholder]);

  useEffect(() => {
    if (slot.visualisationGroup) {
      (async () => {
        if (slot.visualisationGroup?.backgroundImage) {
          // Get the visualisation group background url from Shopify.
          const { data, errors } = await storefront.request(getMediaImageQuery, {
            variables: {
              id: slot.visualisationGroup?.backgroundImage
            }
          });
          if (errors) {
            console.error('Failed to fetch visualisation group background image:', errors);
          }
          setBackgroundImageUrl(data?.node?.image?.url);
        }
      })();
    }
  }, [slot.visualisationGroup]);

  useEffect(() => {
    // Check if we should auto-open: star map slot enabled, star map file exists, and no existing file.
    const shouldTrigger = shouldShowStarMapSlot && starMapFile && !existingFile;

    // @todo: This is a hack to reset the scale to 1 after the editor opens as well as a delay on opening to allow filemask to load in properly. It should be removed when we have a better solution.
    if (shouldTrigger) {
      const timeoutId = setTimeout(() => {
        setShouldAutoOpen(true);
        // Trigger scale reset after editor opens, only when shouldShowStarMapSlot is enabled.
        if (shouldShowStarMapSlot) {
          setResetScaleTrigger((prev) => prev + 1);
        }
      }, 200);

      // Cleanup timeout on unmount or when dependencies change.
      return () => {
        clearTimeout(timeoutId);
        setShouldAutoOpen(false);
      };
    } else {
      // Reset auto-open state if conditions are not met.
      setShouldAutoOpen(false);
    }
  }, [shouldShowStarMapSlot, starMapFile, existingFile]);
  /**
   * Handles file selection from the PhotoEditor component.
   * @param {Object} data - The file selection data containing user and edited images.
   * @param {Object} data.userImage - The original user image data.
   * @param {Object} data.editedImage - The edited image data.
   */
  const handleFileSelect = useCallback(
    (data) => {
      const { editedImage = null } = data ?? {};
      // Handle both the old format and new format.
      const editedImageFile = editedImage?.file || null;
      // const originalImageFile = userImage?.file || null;
      // Build extraImages array - start with the uploaded image.
      const extraImagesArray = [];

      if (editedImageFile) {
        extraImagesArray.push({ data: editedImageFile, type: 'original' });
      }
      // Add star map image if available and shouldShowStarMapSlot is enabled.
      if (shouldShowStarMapSlot && starMapFile) {
        extraImagesArray.push({ data: starMapFile, type: 'star-map' });
      }
      // Update selections with all images (uploaded + star map).
      updateSelections({
        slotId: slot.id,
        itemSelected: editedImageFile, // @TODO: This needs removing as we don't use it - it gets overridden.
        extraImages: extraImagesArray.length > 0 ? extraImagesArray[0] : null,
        supportingImage: editedImageFile
      });
      // If we have multiple images, add the rest.
      for (let i = 1; i < extraImagesArray.length; i++) {
        const currentSelection = selections.slots?.find((s) => s.id === slot.id);
        updateSelections({
          slotId: slot.id,
          itemSelected: currentSelection?.selected || editedImageFile || null,
          extraImages: extraImagesArray[i]
        });
      }
    },
    [updateSelections, slot.id, shouldShowStarMapSlot, starMapFile, selections.slots]
  );

  const { allowedFileTypes, maxFileSize, uiHeading, uiSubheading, isRequired, engraving, fingerprint } = slot;

  const uploadType = engraving ? 'engraving' : 'photo';

  if (!requiresMasking) {
    return (
      <div className={slot.disabled ? styles.disabled : ''}>
        <Label required={isRequired} htmlFor={slot.id}>
          {uiHeading}
        </Label>
        <FileInput
          onFileSelect={handleFileSelect}
          disabled={slot.disabled}
          buttonVariant="primary"
          buttonSize="small"
          buttonColor="orange"
          maxFileSize={maxFileSize}
          id={slot.id}
          selectedFile={existingFile}
          accept={allowedFileTypes?.join(',')}
        />
      </div>
    );
  }

  return (
    <div className={slot.disabled ? styles.disabled : ''}>
      <Label required={isRequired} htmlFor={slot.id}>
        {uiHeading}
      </Label>
      <PhotoEditor
        accept={allowedFileTypes?.join(',')}
        onFileSelect={handleFileSelect}
        disabled={slot.disabled}
        buttonVariant="primary"
        buttonSize="small"
        buttonColor="orange"
        maxFileSize={maxFileSize}
        id={slot.id}
        selectedFile={shouldShowStarMapSlot && starMapFile && !existingFile ? starMapFile : existingFile}
        autoOpen={shouldAutoOpen ? true : undefined}
        label="Choose File"
        stepImageUrl={backgroundImageUrl}
        uploadType={uploadType}
        maskSvgBlob={maskSvgBlob}
        showStarMapSlot={shouldShowStarMapSlot}
        resetScaleTrigger={shouldShowStarMapSlot ? resetScaleTrigger : undefined}
        maskImageConfig={{
          url: slot.targetImagePlaceholder.url,
          width: slot.targetImagePlaceholder.width,
          height: slot.targetImagePlaceholder.height
        }}
        positioning={{
          x: Number(slot.positionX),
          y: Number(slot.positionY),
          scale: Number(slot.scale),
          rotation: Number(slot.rotation)
        }}
        maskZones={slot.imageMaskZones || []}
      />
      {uiSubheading && <p className={'body-sm ' + styles.subheading}>{uiSubheading}</p>}
    </div>
  );
};
